import { memo } from 'react';
import {
  IconButton, Grid, Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import CropIcon from '@mui/icons-material/Crop';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { StaticMedia } from '@client/components/common/Media/StaticMedia';
import type { PublicImageUrl } from '@sda/interfaces/image';
import type { useImageUpload } from './useImageUpload.hook';

const useStyles = makeStyles({ name: 'ImagePreview' })((theme) => ({
  mediaContainer: {
    position: 'relative',
    marginTop: theme.spacing(1),
    outline: 'none',

    '& > div.overlay': {
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: '100%',
      backgroundColor: theme.mixins.alpha(theme.palette.grey[900], 0),
      transition: theme.transitions.create('background-color', {
        easing: theme.transitions.easing.sharp,
        duration: 200,
      }),

      '& > menu': {
        display: 'none',
      },
    },

    '&:hover > div.overlay': {
      backgroundColor: theme.mixins.alpha(theme.palette.grey[900], 0.2),

      '& > menu': {
        display: 'block',
      },
    },
  },
  mediaContainerDraggable: {
    cursor: 'grab',
    touchAction: 'none',
  },
  mediaContainerClickable: {
    cursor: 'pointer',
  },
  media: {
    height: '100%',
    width: '100%',
    position: 'relative',
  },
  removeButton: {
    position: 'absolute',
    top: theme.spacing(1 / 2),
    right: theme.spacing(1 / 2),
  },
  cropButton: {
    position: 'absolute',
    bottom: theme.spacing(1 / 2),
    right: theme.spacing(1 / 2),
  },
  coverImage: {
    padding: theme.spacing(1 / 2, 1),
    position: 'absolute',
    top: theme.spacing(1 / 2),
    left: theme.spacing(1 / 2),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.radii.large,
  },
  coverImageText: {
    opacity: 0.6,
    userSelect: 'none',
  },
  dragHandle: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  fullWidth: {
    borderRadius: theme.radii.large,
    overflow: 'hidden',
  },
}));

interface ImagePreviewProps extends PublicImageUrl {
  readonly disabled: boolean;
  readonly columnWidth: number;
  readonly handleImageRemove: ReturnType<typeof useImageUpload>['handleImageRemove'];
  readonly handleLaunchCropDialog: ReturnType<typeof useImageUpload>['handleLaunchCropDialog'];
  readonly index: number;
  readonly name: string;
  readonly onClick?: () => void;
}

export const ImagePreview = memo<ImagePreviewProps>(({
  disabled: fieldDisabled, handleImageRemove, columnWidth,
  name, onClick, handleLaunchCropDialog, index,
  ...file
}) => {
  const { classes, cx } = useStyles();
  const disabled = fieldDisabled || Boolean(onClick);

  const {
    attributes, listeners, setNodeRef, transform,
    transition, setActivatorNodeRef,
  } = useSortable({ id: file.id, disabled });

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  const clickableProps = onClick ? {
    onClick,
    role: 'button',
  } : {};

  return (
    <Grid
      item
      xs={columnWidth}
      ref={setNodeRef}
      style={style}
    >
      <div
        className={cx(
          classes.mediaContainer,
          !disabled && classes.mediaContainerDraggable,
          Boolean(onClick) && classes.mediaContainerClickable,
          columnWidth === 12 && classes.fullWidth,
        )}
        {...clickableProps}
      >
        <StaticMedia
          className={classes.media}
          images={[file]}
          title="Promotional graphic"
        />

        {!disabled && (
          <div className="overlay">
            <div
              className={classes.dragHandle}
              ref={setActivatorNodeRef}
              {...attributes}
              {...listeners}
            />

            <menu>
              <IconButton
                size="medium"
                color="overlay"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  handleImageRemove(file.path);
                }}
                aria-label="delete image"
                className={classes.removeButton}
              >
                <CloseIcon />
              </IconButton>

              <IconButton
                size="small"
                color="paper"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  handleLaunchCropDialog(file);
                }}
                aria-label="crop image"
                className={classes.cropButton}
              >
                <CropIcon />
              </IconButton>
            </menu>
          </div>
        )}

        {index === 0 && (
          <div className={classes.coverImage}>
            <Typography
              component="span"
              variant="caption"
              color="textPrimary"
              className={classes.coverImageText}
            >
              Cover
            </Typography>
          </div>
        )}
      </div>
    </Grid>
  );
});
